import { createElement } from 'react'
import type { TFunction } from 'i18next'
import type {
  InsightsGeo,
  InsightsGeoTrend, InsightsTrend,
  InsightsGeoExtremes, InsightsExtremes,
  InsightsGeoLatest, InsightsLatest,
  InsightsGeoBiggestChange, InsightsBiggestChange,
  GeoLabelsItem,
  InsightsComparison,
  InsightsCorrelation,
  RevisionsResponse,
  BiggestChange,
  RevisionsResponseBiggestChange,
  DataResponseInsights,
} from '../api' 
import type { Insights } from '../api' 


import { GEO_COLORS, type Observation } from '../types/Stats'
const HIGHLIGHT_COLOR = 'var(--primary-color)'


export interface InsightsResult {
  perGeo:           Record<string, Observation[]>
  perGeoHeaders:    Record<string, Observation>
  comparison:       Observation[]
  comparisonHeader: Observation | null
}

// Type guards

export const isInsights = (i: DataResponseInsights | undefined | null): i is Insights =>
  i != null && 'per_geo' in i

export const isTrend = (t: InsightsGeoTrend): t is InsightsTrend =>
  t != null && 'start_val' in t

export const isExtremes = (e: InsightsGeoExtremes): e is InsightsExtremes =>
  e != null && 'max_val' in e

export const isLatest = (l: InsightsGeoLatest): l is InsightsLatest =>
  l != null && 'latest_val' in l

export const isBiggestChange = (b: InsightsGeoBiggestChange): b is InsightsBiggestChange =>
  b != null && 'change_year' in b


// Formátovanie hodnôt
export function formatValue(value: number, unitLabel: string): string {
  // zaokrúhlenie na 2 desatinné miesta
  const rounded = Math.round(value * 100) / 100
  const formatted = rounded.toLocaleString('cs-CZ', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
  return unitLabel ? `${formatted} ${unitLabel}` : formatted
}

// farebný span pre konkrétne geo 
function geoSpan(geoIndex: number) {
  return createElement('span', { style: { color: GEO_COLORS[geoIndex] } })
}

function highlightSpan() {
  return createElement('span', { style: { color: HIGHLIGHT_COLOR } })
}

// Pozorovania pre Geo
export function buildAverageObservation(
  geoStats:  InsightsGeo,
  unitLabel: string,
): Observation | null {
  if (geoStats.average == null) return null
  return {
    i18nKey: 'insights.tpl.average',
    values: {
      value: formatValue(geoStats.average, unitLabel),
    },
    components: [highlightSpan()],
  }
}

export function buildTrendObservation(
  trend:     InsightsTrend,
  unitLabel: string,
  t:         TFunction,
): Observation {
  const direction = trend.end_val > trend.start_val ? 'up' : 'down'
  return {
    i18nKey: 'insights.tpl.trend',
    values: {
      verb:      t(`insights.direction.verbPast.${direction}`),
      startVal:  formatValue(trend.start_val, unitLabel),
      startYear: trend.start_year,
      endVal:    formatValue(trend.end_val, unitLabel),
      endYear:   trend.end_year,
    },
    components: [
      highlightSpan(), 
    ],
  }
}

export function buildExtremesObservation(
  extremes:  InsightsExtremes,
  unitLabel: string,
): Observation {
  return {
    i18nKey: 'insights.tpl.extremes',
    values: {
      maxVal:  formatValue(extremes.max_val, unitLabel),
      maxYear: extremes.max_year,
      minVal:  formatValue(extremes.min_val, unitLabel),
      minYear: extremes.min_year,
    },
    components: [
      highlightSpan(), 
    ],
  }
}

export function buildLatestObservation(
  latest:    InsightsLatest,
  unitLabel: string,
  t:         TFunction,
): Observation {
  return {
    i18nKey: 'insights.tpl.latest',
    values: {
      latestVal:  formatValue(latest.latest_val, unitLabel),
      latestYear: latest.latest_year,
      diff:       formatValue(Math.abs(latest.diff), unitLabel),
      verbPast:     t(`insights.direction.verbPast.${latest.direction}`),
      prevYear:   latest.prev_year,
    },
    components: [
      highlightSpan(), 
    ],
  }
}

export function buildBiggestChangeObservation(
  change:    InsightsBiggestChange,
  unitLabel: string,
  t:         TFunction,
): Observation {
  return {
    i18nKey: 'insights.tpl.biggest_change',
    values: {
      prevYear:   change.prev_year,
      changeYear: change.change_year,
      verbPast:       t(`insights.direction.verbPast.${change.direction}`),
      prevVal:    formatValue(change.prev_val, unitLabel),
      curVal:     formatValue(change.cur_val, unitLabel),
    },
    components: [
      highlightSpan(), 
    ],
  }
}

// Vstavia všetky pozorovania pre dané geo
export function buildGeoObservations(
  geoStats:  InsightsGeo,
  unitLabel: string,
  t:         TFunction,
): Observation[] {
  const observations: Observation[] = []

  const avg = buildAverageObservation(geoStats, unitLabel)
  if (avg) observations.push(avg)

  if (isTrend(geoStats.trend)) {
    observations.push(buildTrendObservation(geoStats.trend, unitLabel, t))
  }

  if (isExtremes(geoStats.extremes)) {
    observations.push(buildExtremesObservation(geoStats.extremes, unitLabel))
  }

  if (isLatest(geoStats.latest)) {
    observations.push(buildLatestObservation(geoStats.latest, unitLabel, t))
  }

  if (isBiggestChange(geoStats.biggest_change)) {
    observations.push(buildBiggestChangeObservation(geoStats.biggest_change, unitLabel, t))
  }

  return observations
}

// Comparison observation
export function buildComparisonObservation(
  comparisons:     InsightsComparison[],    
  geoOrder:        string[],                
  geoLabels:       Record<string, GeoLabelsItem>,
  unitLabel:       string,
  t:               TFunction,
): Observation | null {
  if (comparisons.length === 0) return null

  const primaryKey   = comparisons[0].primary
  const primaryLabel = geoLabels[primaryKey]?.label ?? primaryKey
  const geoIndexMap  = new Map(geoOrder.map((g, i) => [g, i]))
  const primaryIndex = geoIndexMap.get(primaryKey) ?? 0

  // 1 compare geo -> comparison_1
  if (comparisons.length === 1) {
    const cmp = comparisons[0]
    const direction = cmp.diff > 0 ? 'up' : 'down'
    const compareIndex = geoIndexMap.get(cmp.compare) ?? 0

    return {
      i18nKey: 'insights.tpl.comparison_1',
      values: {
        geo:        primaryLabel,
        diff:       formatValue(Math.abs(cmp.diff), unitLabel),
        dir:  t(`insights.direction.adjective.${direction}`),
        compareGeo: geoLabels[cmp.compare]?.label ?? cmp.compare,
      },
      components: [
        geoSpan(primaryIndex),  // <0> 
        highlightSpan(),        // <1>
        geoSpan(compareIndex),  // <2> 
      ],
    }
  }

  // 2 compare geo -> comparison_2
  const cmp1 = comparisons[0]
  const cmp2 = comparisons[1]
  const dir1 = cmp1.diff > 0 ? 'up' : 'down'
  const dir2 = cmp2.diff > 0 ? 'up' : 'down'
  const compare1Index = geoIndexMap.get(cmp1.compare) ?? 0
  const compare2Index = geoIndexMap.get(cmp2.compare) ?? 0

  return {
    i18nKey: 'insights.tpl.comparison_2',
    values: {
      geo:        primaryLabel,
      diff1:      formatValue(Math.abs(cmp1.diff), unitLabel),
      dir1: t(`insights.direction.adjective.${dir1}`),
      compare1:   geoLabels[cmp1.compare]?.label ?? cmp1.compare,
      diff2:      formatValue(Math.abs(cmp2.diff), unitLabel),
      dir2: t(`insights.direction.adjective.${dir2}`),
      compare2:   geoLabels[cmp2.compare]?.label ?? cmp2.compare,
    },
    components: [
      geoSpan(primaryIndex),    // <0>
      highlightSpan(),          // <1> 
      geoSpan(compare1Index),   // <2> 
      geoSpan(compare2Index),   // <3> 
    ],
  }
}

export function buildCorrelationObservation(
  correlation:       InsightsCorrelation,
  primaryGeoIndex:   number,
  compareGeoIndex:   number,
  geoLabels:         Record<string, GeoLabelsItem>,
  t:                 TFunction,
): Observation {
  return {
    i18nKey: 'insights.tpl.correlation',
    values: {
      geo:   geoLabels[correlation.geo_a]?.label_gen     ?? correlation.geo_a,
      compare:   geoLabels[correlation.geo_b]?.label_gen ?? correlation.geo_b,
      correlationText: t(`insights.correlationStrength.${correlation.strength}`),
      r:            correlation.r.toFixed(2),
    },
    components: [
      geoSpan(primaryGeoIndex),  // <0> 
      geoSpan(compareGeoIndex),  // <1>
      highlightSpan(),           // <2> 
    ],
  }
}

// Headers 
export function buildPerGeoHeader(
  metric:    string,
  geoKey:    string,
  geoIndex:  number,
  geoLabels: Record<string, GeoLabelsItem>,
  yearRange: [number, number],
): Observation {
  return {
    i18nKey: 'insights.tpl.geo_header',
    values: {
      metric:    metric, 
      geo:       geoLabels[geoKey]?.label_lok_prep ?? geoKey,
      yearStart: yearRange[0],
      yearEnd:   yearRange[1],
    },
    components: [
      highlightSpan(),    // <0> 
      geoSpan(geoIndex),  // <1> 
    ],
  }
}

export function buildComparisonHeader(
  geoOrder:  string[],
  geoLabels: Record<string, GeoLabelsItem>,
): Observation | null {
  const [primary, ...compares] = geoOrder
  if (compares.length === 0) return null
  const primaryGen = geoLabels[primary]?.label_gen ?? primary
  const compare1Ins = geoLabels[compares[0]]?.label_ins ?? compares[0]
  if (compares.length === 1) {
    return {
      i18nKey: 'insights.tpl.comparison_header_1',
      values: {
        geo: primaryGen,
        compare1:   compare1Ins,
      },
      components: [
        geoSpan(0),  // <0>
        geoSpan(1),  // <1> 
      ],
    }
  }

  const compare2Ins = geoLabels[compares[1]]?.label_ins ?? compares[1]
  return {
    i18nKey: 'insights.tpl.comparison_header_2',
    values: {
      geo: primaryGen,
      compare1:   compare1Ins,
      compare2:   compare2Ins,
    },
    components: [
      geoSpan(0),  // <0> 
      geoSpan(1),  // <1> 
      geoSpan(2),  // <2> 
    ],
  }
}

export function buildInsights(
  metric: string,
  insights:  Insights,
  geoOrder:  string[],
  geoLabels: Record<string, GeoLabelsItem>,
  yearRange: [number, number],
  unitLabel: string,
  t:         TFunction,
): InsightsResult {
  const perGeo: Record<string, Observation[]> = {}
  const perGeoHeaders: Record<string, Observation> = {}
  for (let i = 0; i < geoOrder.length; i++) {
    const geoKey = geoOrder[i]
    const stats = insights.per_geo[geoKey]
    if (!stats) continue

    perGeo[geoKey] = buildGeoObservations(stats, unitLabel, t)
    perGeoHeaders[geoKey] = buildPerGeoHeader(metric, geoKey, i, geoLabels, yearRange)
  }

  // Comparison tab 
  const comparison: Observation[] = []
  const primaryIndex = 0
  const geoIndexMap = new Map(geoOrder.map((g, i) => [g, i]))

  const cmpObs = buildComparisonObservation(
    insights.comparisons, geoOrder, geoLabels, unitLabel, t,
  )
  if (cmpObs) comparison.push(cmpObs)
  for (const cor of insights.correlations) {
    const compareIndex = geoIndexMap.get(cor.geo_b) ?? 0
    comparison.push(buildCorrelationObservation(
      cor, primaryIndex, compareIndex, geoLabels, t,
    ))
  }
  const comparisonHeader = comparison.length > 0
    ? buildComparisonHeader(geoOrder, geoLabels)
    : null

  return { perGeo, perGeoHeaders, comparison, comparisonHeader }
}

// Revisions
export const isBiggestRevisionChange = (
  b: RevisionsResponseBiggestChange | undefined | null,
): b is BiggestChange =>
  b != null && 'year' in b

// Vráti dátum v ISO 8601 tvare YYYY-MM-DD 
export function formatDate(iso: string): string {
  return iso.slice(0, 10)
}

// Revision buildery
export function buildRevisionUnavailableObservation(): Observation {
  return {
    i18nKey: 'revisions.unavailable',
    values:  {},
    components: [],
  }
}

export function buildRevisionSinceObservation(
  firstRecorded: string,
): Observation {
  return {
    i18nKey: 'revisions.since',
    values: {
      date: formatDate(firstRecorded),
    },
    components: [
      highlightSpan(),  // <0>
    ],
  }
}

export function buildRevisionNoRevisionsObservation(): Observation {
  return {
    i18nKey: 'revisions.noRevisions',
    values:  {},
    components: [],
  }
}

export function buildRevisionRevisedCountObservation(
  count: number,
): Observation {
  return {
    i18nKey: 'revisions.revisedCount',
    values: {
      count,
    },
    components: [
      highlightSpan(),  // <0>
    ],
  }
}

export function buildRevisionExtensionEventsObservation(
  count: number,
): Observation {
  return {
    i18nKey: 'revisions.extensionEvents',
    values: {
      count,
    },
    components: [
      highlightSpan(),  // <0>
    ],
  }
}

export function buildRevisionModificationEventsObservation(
  count: number,
): Observation {
  return {
    i18nKey: 'revisions.modificationEvents',
    values: {
      count,
    },
    components: [
      highlightSpan(),  // <0>
    ],
  }
}

export function buildRevisionNoNetChangesObservation(): Observation {
  return {
    i18nKey: 'revisions.noNetChanges',
    values:  {},
    components: [],
  }
}

export function buildRevisionBiggestChangeObservation(
  change:    BiggestChange,
  unitLabel: string,
): Observation {
  return {
    i18nKey: 'revisions.biggestChange',
    values: {
      year: change.year,
      old:  formatValue(change.old_value, unitLabel),
      new:  formatValue(change.new_value, unitLabel),
      date: formatDate(change.revision_date),
    },
    components: [
      highlightSpan(),  // <0> 
      highlightSpan(),  // <1> 
      highlightSpan(),  // <2> 
      highlightSpan(),  // <3> 
    ],
  }
}

// Orchestrator pre revisions 
export function buildRevisionsObservations(
  revisions: RevisionsResponse | null,
  isError:   boolean,
  unitLabel: string,
): Observation[] {
  if (isError) {
    return [buildRevisionUnavailableObservation()]
  }

  if (!revisions || revisions.version_count === 0) {
    return [buildRevisionUnavailableObservation()]
  }

  const observations: Observation[] = []

  // 1. Since
  if (revisions.first_recorded) {
    observations.push(buildRevisionSinceObservation(revisions.first_recorded))
  }

  // 2. Revised count alebo No revisions
  const revCount = revisions.version_count - 1
  if (revCount === 0) {
    observations.push(buildRevisionNoRevisionsObservation())
    return observations 
  }

  observations.push(buildRevisionRevisedCountObservation(revCount))

  // 3. Net changes
  if (revisions.extension_events === 0 && revisions.modification_events === 0) {
    observations.push(buildRevisionNoNetChangesObservation())
  } else {
    if (revisions.extension_events > 0) {
      observations.push(buildRevisionExtensionEventsObservation(revisions.extension_events))
    }
    if (revisions.modification_events > 0) {
      observations.push(buildRevisionModificationEventsObservation(revisions.modification_events))
    }
  }

  // 4. Biggest change
  if (isBiggestRevisionChange(revisions.biggest_change)) {
    observations.push(buildRevisionBiggestChangeObservation(revisions.biggest_change, unitLabel))
  }

  return observations
}